import dynamic, { DynamicOptions } from 'next/dynamic';

export { getPageContentsWithProducts } from './CarouselProducts';

export enum PrismicSliceAuthContext {
  ALL = 'All',
  AUTHENTICATED = 'Authenticated Only',
  MEMBER = 'Member Only',
  ANONYMOUS = 'Anonymous Only',
  REGISTERED = 'Registered Only',
}

enum SliceType {
  FULLMOBILEPAGE = 1,
  HEADER = 2,
  FULLPAGE = 4,
}

type ISliceOptions = Omit<DynamicOptions, 'loader'> & {
  type?: SliceType;
};

type ComponentRegistration = {
  name: string;
  load: () => unknown;
  options?: ISliceOptions;
};

const slices = {};
const fullMobilePageSlices: string[] = [];
const sliceAsHeader: string[] = [];
const fullPageSlices: string[] = [];

export const getSliceByType = (type: string) => {
  return slices[type];
};
export const isMobilePageSlice = (sliceType: string) =>
  fullMobilePageSlices.includes(sliceType);
export const isHeaderSlice = (sliceType: string) =>
  sliceAsHeader.includes(sliceType);
export const isFullPageSlice = (sliceType: string) =>
  fullPageSlices.includes(sliceType);

const handleSliceType = (type: SliceType, sliceKey: string) => {
  if (type & SliceType.FULLPAGE) fullPageSlices.push(sliceKey);
  if (type & SliceType.FULLMOBILEPAGE) fullMobilePageSlices.push(sliceKey);
  if (type & SliceType.HEADER) sliceAsHeader.push(sliceKey);
};

const registerComponent = ({
  name,
  load,
  options = {},
}: ComponentRegistration) => {
  if (!name || !load) return;
  slices[name] = load();

  if (!options.type) return;
  handleSliceType(options.type, name);
};

registerComponent({
  name: 'cta_3_cards',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./Cta3Cards')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'Cta3Cards',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/Cta3Cards')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'title_pictos',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitlePictos')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'cta_image_multiple_title_text',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./CtaImageMultipleText')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'CtaImageMultipleText',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/CtaImageMultipleText')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'image_leaf_paragraph',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./ImageLeafParagraph')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'ImageLeafParagraph',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/ImageLeafParagraph')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'banner',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./Banner')).default,
        };
      },
      { ssr: false },
    ),
});

registerComponent({
  name: 'Banner',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/Banner')).default,
        };
      },
      { ssr: false },
    ),
});

registerComponent({
  name: 'title_and_intro',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitleIntro')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'TitleAndIntro',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/TitleAndIntro')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'rich_text_card',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./RichTextCard')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'RichTextCard',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/RichTextCard')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'CtaButton',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./CtaButton')).CtaButton,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'title_multi_image_text',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitleMultiImageText')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'TitleMultiImageText',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/TitleMultiImageText')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'optional_subtitle_optional_paragraph_button',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitleTextButton')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'TitleTextButton',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/TitleTextButton')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'shop_title_list_image_subtitle_paragraph_end_list',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitleTextMultiColumnImageTitleText'))
            .default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'TitleTextMultiColumnImageTitle',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/TitleTextMultiColumnImageTitle'))
            .default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'shop_centered_picture_subtitle_paragraph_endcenter',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./PictoTextRow')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'title_text_centered',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitleTextCenteredColoredCard')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'TitleTextCenteredColoredCard',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/TitleTextCenteredColoredCard'))
            .default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'small_centered_text',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./SmallCenteredText')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'SmallCenteredText',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/SmallCenteredText')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'shop_subtitle_list_leaf_picture_subsubtitle_paragraph_flip_endlist',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitleLeafImageTextRow')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'TitleLeafImageTextRow',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/TitleLeafImageTextRow')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'multislidercalculator',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./MultiSliderCalculator')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'MultiSliderCalculator',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/MultiSliderCalculator')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'widget_trustpilot',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./WidgetTrustpilot')).default,
        };
      },
      { ssr: false },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'WidgetTrustpilot',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/WidgetTrustpilot')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'products_carousel',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./CarouselProducts')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'ProductsCarousel',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/ProductsCarousel')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'carousel_product_bundles',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./CarouselProductBundles')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'CarouselProductBundles',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/CarouselProductBundles')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'multiple_products_carousels',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./MultipleCarouselProducts')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'MultipleProductsCarousels',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/MultipleProductsCarousels')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'jumbotron',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./Jumbotron')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'Jumbotron',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/Jumbotron')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'description',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./Description')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'Description',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/Description')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'faq',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./FAQ')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'Faq',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/Faq')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'title_accordion_leaf',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitleAccordionLeaf')).default,
        };
      },
      { ssr: true },
    ),
});

// TODO: To remove with title_accordion_leaf above
registerComponent({
  name: 'shop_leaf_subtitle_list_subsubtitle_paragraph_foldable_endlist',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitleAccordionLeaf')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'TitleAccordionLeaf',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/TitleAccordionLeaf')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'shop_background_image_list_subtitle_leaf_value_leaf_subsubtitle_endlist_button',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./TitleMultiRowCardsButton')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'TitleMultiRowCardsButton',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/TitleMultiRowCardsButton')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'carousel_fresh_products',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./CarouselFreshProducts')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'CarouselFreshProducts',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/CarouselFreshProducts')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'herobannermember',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./HeroBannerMember')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'HeroBannerMember',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/HeroBannerMember')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'rowcardsv2',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./RowCard')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'RowCards',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/RowCards')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'cardmembershipwithtrustpilot',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./CardMembershipWithTrustpilot')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'privatelabelcta',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./PrivateLabelCTA')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'herobanner',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./HeroBanner')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'HeroBanner',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/HeroBanner')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'scrolltotop',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@components/ScrollToTop')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'ScrollToTop',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/ScrollToTop')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'mobile_store_redirection',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/MobileStoreRedirection')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'redirection_handler',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./RedirectionHandler')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'RedirectionHandler',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/RedirectionHandler')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'collectioncards',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./CollectionCards')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'CollectionCards',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/CollectionCards')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'subscribe_newsletter',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./SubscribeNewsletter')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'register_email',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./RegisterEmail')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'pillarcards',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./PillarCards')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'first_order_gift',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./FirstOrderGift')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'horizontalbanner',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./HorizontalBanner')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'HorizontalBanner',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (
            await import('@monofront/slice-machine/slices/HorizontalBanner')
          ).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'SubscriptionWithTimeline',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./SubscriptionWithTimeline')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'subscriptionform',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./SubscriptionForm')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'SubscriptionForm',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/SubscriptionForm')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'cardscolumn',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./CardsColumn')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'CheckoutCardsColumn',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/CheckoutCardsColumn')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'youtube_video',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./YoutubeVideo')).default,
        };
      },
      { ssr: false },
    ),
});

registerComponent({
  name: 'mobile_app_banner',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./MobileAppBanner')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'MobileAppBanner',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/MobileAppBanner')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'recommendations',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./Recommendations')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'Recommendations',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/Recommendations')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'numbered_steps_with_anchor_links',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./StepsWithAnchors')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'NumberedStepsWithAnchorLinks',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/NumberedStepsWithAnchorLinks'))
            .default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'editoriallistandbubble',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./EditorialListAndBubble')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'EditorialListAndBubble',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/EditorialListAndBubble')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'editoriallistandimage',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./EditorialListAndImage')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'EditorialListAndImage',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/EditorialListAndImage')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'frequently_bought_products_carousel',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./FrequentlyBoughtProductsCarousel')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'brands_logo',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./BrandsLogoList')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'BrandsLogoList',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/BrandsLogoList')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'bubble_in_tabs',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./BubblesInTabs')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'BubblesInTabs',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/BubblesInTabs')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'app_store_banner',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./BannerAppSmart')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'dual_bubbles',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./DualBubbles')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'DualBubbles',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/DualBubbles')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'list_box',
  load: () =>
    dynamic(async () => {
      return {
        default: (await import('./ListBox')).default,
      };
    }),
});

registerComponent({
  name: 'delivery_fees_table',
  load: () =>
    dynamic(async () => {
      return {
        default: (await import('./DeliveryFeesValuesTable')).default,
      };
    }),
});

registerComponent({
  name: 'single_bubble',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./SingleBubble')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'SingleBubble',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/SingleBubble')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'FlashSalesCart',
  load: () =>
    dynamic(async () => {
      return {
        default: (await import('@slices/FlashSalesCart')).default,
      };
    }),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'rich_text_zone',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/RichTextZone')).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'information_modal',
  load: () =>
    dynamic(async () => {
      return {
        default: (
          await import('@monofront/slice-machine/slices/InformationModal')
        ).default,
      };
    }),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'PreferredMultipleCarouselsProducts',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('@slices/PreferredMultipleCarouselsProducts'))
            .default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'InformationBlock',
  load: () =>
    dynamic(async () => import('@slices/DeliveryBarInformationBlock')),
});

registerComponent({
  name: 'DeliveryBarAddressInput',
  load: () => dynamic(async () => import('@slices/DeliveryBarAddressInput')),
});

registerComponent({
  name: 'DeliveryBarCta',
  load: () => dynamic(async () => import('@slices/DeliveryBarCta')),
});

registerComponent({
  name: 'informative_box',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (
            await import('@monofront/slice-machine/slices/InformativeBox')
          ).default,
        };
      },
      { ssr: true },
    ),
});

registerComponent({
  name: 'hero_hook',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./HeroHook')).HeroHook,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'image',
  load: () =>
    dynamic(
      async () => {
        return {
          default: (await import('./Image')).default,
        };
      },
      { ssr: true },
    ),
  options: {
    type: SliceType.FULLPAGE,
  },
});

registerComponent({
  name: 'average_product_basket',
  load: () =>
    dynamic(async () => import('@slices/AverageProductBasket'), { ssr: true }),
});

registerComponent({
  name: 'annual_savings',
  load: () => dynamic(async () => import('@slices/ContentStepSlice')),
});

registerComponent({
  name: 'household_size_selector',
  load: () =>
    dynamic(async () => import('@slices/HouseholdSizeSelector'), {
      ssr: false,
    }),
});
